import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  memo,
} from "react";
import { Link, Redirect } from "react-router-dom";
import axios from "axios";
import {
  FaAward,
  FaBell,
  FaBus,
  FaCalendarAlt,
  FaCalendarCheck,
  FaCertificate,
  FaChartBar,
  FaChartLine,
  FaChartPie,
  FaClipboardCheck,
  FaClipboardList,
  FaDatabase,
  FaEdit,
  FaFileAlt,
  FaFilePdf,
  FaFolderOpen,
  FaIdBadge,
  FaIdCard,
  FaMoneyBillWave,
  FaPrint,
  FaReceipt,
  FaSearch,
  FaSignature,
  FaSyncAlt,
  FaTimes,
  FaTrophy,
  FaUserTie,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import BackButton from "../ChildComponent/BackButton";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const FILTER_ALL = "ALL";
const FILTER_REPORT = "REPORT";
const FILTER_PRINTABLE = "PRINTABLE";
const FILTER_BLANK = "BLANK";

const ICON_MAP = {
  ReportViewClass: FaChartLine,
  StudentAttendanceReport: FaCalendarCheck,
  MarkEntryForm: FaEdit,
  MarkEntryCheckList: FaClipboardCheck,
  ExamSchedule: FaCalendarAlt,
  StudentDueList: FaMoneyBillWave,
  CollectionReport: FaChartBar,
  UdiseReport: FaDatabase,
  TransportReport: FaBus,
  StatementManager: FaChartPie,
  PrintableDocs: FaFilePdf,
  PdAdmissionForm: FaFileAlt,
  PrintableDocsAdmitCard: FaIdCard,
  PrintableDocsResult: FaFileAlt,
  PrintableDocsBlank: FaFileAlt,
  PrintableDocsTopper: FaTrophy,
  PrintableDocsIdCard: FaIdBadge,
  PrintableDocsStaffId: FaUserTie,
  PrintableDocsEvent: FaCalendarAlt,
  PrintableDocsDeskSlip: FaClipboardList,
  AppreciationCertificate: FaCertificate,
  StudentCertificate: FaCertificate,
  StudentIdCard: FaIdCard,
  CompetitionAward: FaAward,
  ExamSignSheet: FaSignature,
  DemandSlip: FaReceipt,
  UdiseForm: FaClipboardCheck,
  DemandNotice: FaBell,
  StaffOfferLetter: FaFileAlt,
  LetterHead: FaFileAlt,
};

const TYPE_DEFAULT_ICON = {
  REPORT: FaChartBar,
  PRINTABLE: FaPrint,
  BLANK: FaFileAlt,
};

const TYPE_ACCENT = {
  REPORT: COLORS.card_orange || "#E07A2F",
  PRINTABLE: COLORS.pesto_green || "#00AA8D",
  BLANK: COLORS.card_amber || "#FFB300",
};

//Created By Cursor
function resolveItemType(item) {
  const raw = String(item?.rpd_type || "").toUpperCase();
  if (raw === "REPORT") return FILTER_REPORT;
  if (raw === "PRINTABLE") return FILTER_PRINTABLE;
  return FILTER_BLANK;
}

//Created By Cursor
function getItemIcon(item) {
  const name = item?.component_name || "";
  const type = resolveItemType(item);
  return ICON_MAP[name] || TYPE_DEFAULT_ICON[type] || FaFileAlt;
}

//Created By Cursor
function ReportTile({ item }) {
  const type = resolveItemType(item);
  const Icon = getItemIcon(item);
  const accent = TYPE_ACCENT[type] || COLORS.primary_color_shade5;
  const docType = encodeURIComponent(item.document_type || "");
  const to = item.document_type
    ? `/AdminMain/${item.component_name}/${docType}`
    : `/AdminMain/${item.component_name}`;

  return (
    <Link
      to={to}
      className={`sm-quick-link smhub-quick-link rmhub-tile rmhub-tile--${type.toLowerCase()}`}
      style={{ "--rmhub-accent": accent }}
      aria-label={item.caption || item.component_name}
    >
      <span className="sm-quick-link__chevron" aria-hidden>
        ›
      </span>
      <span className="sm-quick-link__icon" style={{ background: accent }}>
        <Icon />
      </span>
      <span className="sm-quick-link__label">{item.caption || "Untitled"}</span>
      <span className={`rmhub-tile__type rmhub-tile__type--${type.toLowerCase()}`}>
        {type === FILTER_REPORT
          ? "Report"
          : type === FILTER_PRINTABLE
          ? "Printable"
          : "Form"}
      </span>
    </Link>
  );
}

const ReportTileMemo = memo(ReportTile);

//Created By Cursor
function SectionPanel({ step, title, subtitle, count, children, empty, tone }) {
  return (
    <section
      className={`smhub-panel rmhub-panel${tone ? ` rmhub-panel--${tone}` : ""}`}
      aria-label={title}
    >
      <div className="smhub-panel__head">
        <span className="smhub-panel__step" aria-hidden>
          {step}
        </span>
        <div className="min-w-0">
          <h2 className="smhub-panel__title">
            {title}
            <span className="rmhub-count">{count}</span>
          </h2>
          <p className="smhub-panel__sub">{subtitle}</p>
        </div>
      </div>
      {empty ? (
        <div className="rmhub-empty-inline">No items in this group.</div>
      ) : (
        <div className="sm-quick-links__grid rmhub-grid">{children}</div>
      )}
    </section>
  );
}

export default function ReportManagement() {
  const loggedIn = localStorage.getItem("token") != null;
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [allItems, setAllItems] = useState([]);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState(FILTER_ALL);

  //Created By Cursor
  const fetchReports = useCallback(async ({ soft = false } = {}) => {
    const schoolCode = SessionManager.getSchoolCode();
    const staff = SessionManager.getStaffDetails();
    const staffId = staff?.staff_id || "";
    if (!schoolCode || !staffId) {
      setError("Staff session is missing. Please login again.");
      setLoading(false);
      return;
    }

    if (soft) setRefreshing(true);
    else setLoading(true);

    try {
      const res = await axios.get(`${API_BASE()}getReportMaster.php`, {
        params: {
          staff_id: staffId,
          school_code: schoolCode,
          system_code: process.env.REACT_APP_SYSTEM_CODE,
        },
        timeout: 30000,
      });

      if (res.data?.api_status === "OK") {
        const rows = Array.isArray(res.data.data) ? res.data.data : [];
        setAllItems(rows);
        setError("");
      } else {
        setAllItems([]);
        const remark = res.data?.api_remark || "Failed to load reports.";
        setError(remark);
        myToast.notify_warning(remark);
      }
    } catch (err) {
      console.error("[ReportManagement] fetch error", err);
      setAllItems([]);
      const msg =
        err.response?.data?.message || err.message || "Failed to fetch reports";
      setError(msg);
      myToast.notify_error(msg);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchReports();
  }, [fetchReports]);

  const categorized = useMemo(() => {
    const reports = [];
    const printables = [];
    const blankForms = [];
    for (const item of allItems) {
      const type = resolveItemType(item);
      if (type === FILTER_REPORT) reports.push(item);
      else if (type === FILTER_PRINTABLE) printables.push(item);
      else blankForms.push(item);
    }
    return { reports, printables, blankForms };
  }, [allItems]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const match = (item) => {
      if (typeFilter !== FILTER_ALL && resolveItemType(item) !== typeFilter) {
        return false;
      }
      if (!q) return true;
      const hay = `${item.caption || ""} ${item.component_name || ""} ${
        item.document_type || ""
      } ${item.rpd_type || ""}`.toLowerCase();
      return hay.includes(q);
    };
    return {
      reports: categorized.reports.filter(match),
      printables: categorized.printables.filter(match),
      blankForms: categorized.blankForms.filter(match),
    };
  }, [categorized, searchQuery, typeFilter]);

  const totals = useMemo(
    () => ({
      reports: categorized.reports.length,
      printables: categorized.printables.length,
      blankForms: categorized.blankForms.length,
      all: allItems.length,
    }),
    [categorized, allItems.length]
  );

  const filteredTotal =
    filtered.reports.length +
    filtered.printables.length +
    filtered.blankForms.length;

  const showReports =
    typeFilter === FILTER_ALL || typeFilter === FILTER_REPORT;
  const showPrintables =
    typeFilter === FILTER_ALL || typeFilter === FILTER_PRINTABLE;
  const showBlanks = typeFilter === FILTER_ALL || typeFilter === FILTER_BLANK;

  if (!loggedIn) return <Redirect to="/Login" />;

  return (
    <div
      className="content payroll-page sm-page smhub-page rmhub-page"
      style={{ background: COLORS.white, fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}> Report Management</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell smhub-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaChartBar />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Report Management</h1>
              <p className="sm-hero__sub">
                Reports, printables & blank forms
                {totals.all ? ` · ${totals.all} available` : ""}
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <label className="smhub-search rmhub-hero-search">
              <FaSearch className="smhub-search__icon" aria-hidden />
              <input
                type="search"
                placeholder="Search reports…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search reports"
                disabled={loading}
              />
              {searchQuery ? (
                <button
                  type="button"
                  className="smhub-search__clear"
                  onClick={() => setSearchQuery("")}
                  aria-label="Clear search"
                >
                  <FaTimes aria-hidden />
                </button>
              ) : null}
            </label>
            <button
              type="button"
              onClick={() => fetchReports({ soft: true })}
              disabled={loading || refreshing}
              className={`pp-icon-btn${refreshing ? " spinning" : ""}`}
              title="Refresh"
              aria-label="Refresh reports"
            >
              <FaSyncAlt aria-hidden />
            </button>
          </div>
        </header>

        <div className="smhub-body rmhub-body">
          {loading ? (
            <div className="rmhub-loading">
              <MyLoader />
            </div>
          ) : error && allItems.length === 0 ? (
            <div className="rmhub-error" role="alert">
              <h2 className="rmhub-error__title">Couldn’t load reports</h2>
              <p className="rmhub-error__text">{error}</p>
              <button
                type="button"
                className="rmhub-error__btn"
                onClick={() => fetchReports()}
              >
                Try again
              </button>
            </div>
          ) : (
            <>
              <div className="rmhub-filters" role="tablist" aria-label="Filter by type">
                {[
                  { id: FILTER_ALL, label: "All" },
                  { id: FILTER_REPORT, label: "Reports" },
                  { id: FILTER_PRINTABLE, label: "Printables" },
                  { id: FILTER_BLANK, label: "Forms" },
                ].map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    role="tab"
                    aria-selected={typeFilter === f.id}
                    className={`rmhub-filter${
                      typeFilter === f.id ? " is-active" : ""
                    }`}
                    onClick={() => setTypeFilter(f.id)}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

              {filteredTotal === 0 ? (
                <div className="rmhub-empty">
                  <FaFolderOpen aria-hidden />
                  <h2>No matches</h2>
                  <p>
                    {searchQuery || typeFilter !== FILTER_ALL
                      ? "Try another search or filter."
                      : "No reports, printables, or forms are available."}
                  </p>
                </div>
              ) : (
                <>
                  {showReports ? (
                    <SectionPanel
                      step="01"
                      title="Reports"
                      subtitle="Analytical & list reports"
                      count={filtered.reports.length}
                      empty={filtered.reports.length === 0}
                      tone="report"
                    >
                      {filtered.reports.map((item) => (
                        <ReportTileMemo
                          key={`${item.component_name}_${item.document_type}_r`}
                          item={item}
                        />
                      ))}
                    </SectionPanel>
                  ) : null}

                  {showPrintables ? (
                    <SectionPanel
                      step="02"
                      title="Printables"
                      subtitle="Certificates, cards & slips"
                      count={filtered.printables.length}
                      empty={filtered.printables.length === 0}
                      tone="printable"
                    >
                      {filtered.printables.map((item) => (
                        <ReportTileMemo
                          key={`${item.component_name}_${item.document_type}_p`}
                          item={item}
                        />
                      ))}
                    </SectionPanel>
                  ) : null}

                  {showBlanks ? (
                    <SectionPanel
                      step="03"
                      title="Blank forms"
                      subtitle="Templates & blank documents"
                      count={filtered.blankForms.length}
                      empty={filtered.blankForms.length === 0}
                      tone="blank"
                    >
                      {filtered.blankForms.map((item) => (
                        <ReportTileMemo
                          key={`${item.component_name}_${item.document_type}_b`}
                          item={item}
                        />
                      ))}
                    </SectionPanel>
                  ) : null}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
